import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Briefcase, Plus, ArrowRight, FileUp, MessageSquare } from "lucide-react";

/**
 * EmployeeSubmissions — replaces the old "Submit Work" link that just dumped
 * interns at /student-upload. Each kanban task can have a collaboration
 * workspace (chat + files + meetings) attached. Tasks without a workspace
 * yet show an "Open workspace" button that auto-provisions a guidance-style
 * record on first click. Tasks WITH a workspace open the existing
 * GuidanceWorkspace UI.
 */
export default function EmployeeSubmissions() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    api.get("/employee/tasks").then((r) => setTasks(r.data || [])).finally(() => setLoading(false));
  }, []);

  const openWorkspace = async (t) => {
    setOpening(t.id);
    try {
      const r = await api.post(`/employee/tasks/${t.id}/workspace`);
      if (r.data?.guidance_id) {
        window.location.href = `/dashboard/tasks/${r.data.guidance_id}`;
      } else {
        toast.error("Couldn't open workspace");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open workspace");
    } finally { setOpening(null); }
  };

  return (
    <div data-testid="employee-submissions-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Work <em className="italic text-kb-primary">submissions</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Open a collaboration workspace for each task — discuss, drop files, schedule calls, share drafts and submit deliverables in one place.
      </p>

      <div className="mt-6 flex items-center justify-between">
        <h2 className="font-serif text-xl text-kb-ink">Your tasks</h2>
        <Link to="/dashboard/employee/kanban" className="kb-btn-outline text-sm">
          <Plus className="w-4 h-4" /> New task
        </Link>
      </div>

      {loading ? <div className="mt-6 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        tasks.length === 0 ? (
          <div className="mt-6 text-center py-12 border border-dashed border-slate-300 rounded-sm bg-white">
            <Briefcase className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-kb-ink-muted">No tasks yet. Add one from the Kanban board to start submitting work.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="kb-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`submission-task-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-kb-ink">{t.title}</div>
                  <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">
                    {t.status?.replace("_", " ")} · {t.priority}
                    {t.due_date && <> · due {t.due_date}</>}
                    {t.assigner_name && t.assignee_user_id === user?.user_id && t.assigner_user_id !== user?.user_id && <> · from {t.assigner_name}</>}
                  </div>
                </div>
                {t.guidance_id ? (
                  <Link to={`/dashboard/tasks/${t.guidance_id}`} className="kb-btn-primary text-xs" data-testid={`submission-task-${t.id}-open`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Open workspace <ArrowRight className="w-3 h-3" />
                  </Link>
                ) : (
                  <button onClick={() => openWorkspace(t)} disabled={opening === t.id}
                    className="kb-btn-outline text-xs" data-testid={`submission-task-${t.id}-create`}>
                    <FileUp className="w-3.5 h-3.5" /> {opening === t.id ? "Opening…" : "Open workspace"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
